<template>
  <!-- Vai para o `body`: a tela do app é um contexto de empilhamento próprio, e dentro
       dele a barra de abas pintava por cima da folha — o rodapé dela ficava cortado.
       Fora, ela cobre a tela inteira, que é o que uma folha modal precisa fazer. -->
  <Teleport to="body">
    <!-- Tutorial: um passo por vez, com a mesma partida de exemplo para todo mundo. Separado
         da ajuda de propósito — a ajuda é consulta, isto é a primeira vez. -->
    <div class="scrim" @click.self="signOut">
      <div class="sheet" role="dialog" aria-modal="true" aria-label="Tutorial">
        <div class="grab" />

        <header>
          <span class="lbl">tutorial · {{ index + 1 }} de {{ steps.length }}</span>
          <h3>{{ step.title }}</h3>
        </header>

        <!-- A ilustração é a própria partida de exemplo, montada com o mesmo vocabulário
             visual do tabuleiro: cadeia, casas e a conta dos pontos. -->
        <div class="stage">
          <div class="chain">
            <span
              v-for="(item, i) in step.chain"
              :key="i"
              class="link"
              :class="item.state"
            >{{ item.text }}</span>
          </div>

          <div v-if="step.slots" class="slots">
            <span
              v-for="(slot, i) in step.slots"
              :key="i"
              class="slot"
              :class="slot.state"
            >{{ slot.letra }}</span>
          </div>

          <p v-if="step.account" class="account">
            <AppIcon name="star" size="sm" filled />
            <span v-html="step.account" />
          </p>
        </div>

        <p class="text" v-html="step.text" />

        <div class="dots" aria-hidden="true">
          <span v-for="(p, i) in steps" :key="i" :class="{ on: i === index }" />
        </div>

        <div class="actions">
          <button v-if="index > 0" class="btn btn-outline" type="button" @click="index -= 1">
            Voltar
          </button>
          <button v-else class="btn btn-outline" type="button" @click="signOut">Pular</button>

          <button class="btn btn-cta" type="button" @click="advance">
            <AppIcon :name="last ? 'gamepad' : 'enter'" size="sm" />
            {{ last ? 'Bora jogar' : 'Próximo' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import AppIcon from '../ui/AppIcon.vue';

const HELP_SEEN_KEY = 'sentido:help-seen';

/** A partida de exemplo. É a mesma para todo mundo, como manda a Frente 7 do roadmap. */
const PASSOS = [
  {
    title: 'Cinco palavras, um fio',
    chain: [
      { text: 'COSMOS', state: 'ok' },
      { text: '?????', state: 'off' },
      { text: '?????', state: 'off' },
      { text: '?????', state: 'off' },
      { text: '?????', state: 'off' },
    ],
    text: 'A chain tem cinco palavras ligadas por sentido. A primeira é dada — é a <b>âncora</b>. '
      + 'As outras quatro você adivinha, em ordem.',
  },
  {
    title: 'A pista é a palavra anterior',
    chain: [
      { text: 'COSMOS', state: 'ok' },
      { text: 'G······', state: 'on' },
      { text: '?????', state: 'off' },
      { text: '?????', state: 'off' },
      { text: '?????', state: 'off' },
    ],
    slots: [
      { letra: 'G', state: 'dada' },
      { letra: '', state: 'cursor' },
      { letra: '', state: '' },
      { letra: '', state: '' },
      { letra: '', state: '' },
      { letra: '', state: '' },
      { letra: '', state: '' },
    ],
    account: '6 letras escondidas + 1 × 10 = <b>70 pontos</b>',
    text: 'O que se liga a <i>Cosmos</i>? A primeira letra vem de graça e as casas mostram quantas '
      + 'faltam. Acertar cedo vale mais.',
  },
  {
    title: 'Errou? O jogo revela mais uma',
    chain: [
      { text: 'COSMOS', state: 'ok' },
      { text: 'GA·····', state: 'on' },
      { text: '?????', state: 'off' },
      { text: '?????', state: 'off' },
      { text: '?????', state: 'off' },
    ],
    slots: [
      { letra: 'G', state: 'dada' },
      { letra: 'A', state: 'paga' },
      { letra: '', state: 'cursor' },
      { letra: '', state: '' },
      { letra: '', state: '' },
      { letra: '', state: '' },
      { letra: '', state: '' },
    ],
    account: 'agora vale <b>60 pontos</b>',
    text: 'Cada erro abre uma letra e <b class="warn">derruba 10 pontos</b> — a letra em âmbar é a '
      + 'que você pagou para ver. Errar quando sobra só uma escondida encerra a partida, e a chain '
      + 'nunca aparece inteira.',
  },
  {
    title: 'Acertou? A pista muda',
    chain: [
      { text: 'COSMOS', state: 'ok' },
      { text: 'GALÁXIA', state: 'ok' },
      { text: 'E······', state: 'on' },
      { text: '?????', state: 'off' },
      { text: '?????', state: 'off' },
    ],
    text: 'A partir daqui a pista é <i>Galáxia</i>, não <i>Cosmos</i>. É sempre a palavra anterior '
      + 'que puxa a próxima.',
  },
  {
    title: 'Três jeitos de jogar',
    chain: [
      { text: 'DIÁRIA', state: 'ok' },
      { text: 'TREINO', state: 'on' },
      { text: 'TRILHA', state: 'on' },
    ],
    text: '<b>Diária</b> — uma chain por dia, igual para todo mundo; é a que pontua o ranking e '
      + 'segura a sequência. <b>Treino</b> — quantas quiser, sem pontuar. <b>Trilha</b> — fases em '
      + 'ordem, com estrelas e energia.',
  },
];

export default {
  name: 'TutorialSheet',
  components: { AppIcon },
  props: {
    /** `true` na abertura automática da primeira visita; `false` quando é revisão. */
    firstTime: { type: Boolean, default: false },
  },
  emits: ['close'],
  data() {
    return { index: 0, steps: PASSOS };
  },
  computed: {
    step() {
      return this.steps[this.index];
    },
    last() {
      return this.index === this.steps.length - 1;
    },
  },
  methods: {
    advance() {
      if (!this.last) {
        this.index += 1;
        return;
      }
      this.signOut();
    },
    signOut() {
      // Só a abertura automática marca. Rever pelo botão da ajuda não pode mexer numa
      // preferência que o jogador já tinha dado — era o defeito da folha única.
      if (this.firstTime) {
        try {
          localStorage.setItem(HELP_SEEN_KEY, '1');
        } catch {
          // modo privado, ou armazenamento cheio
        }
      }
      this.$emit('close');
    },
  },
};

/** Se o tutorial já rodou neste navegador. */
export function tutorialJaVisto() {
  try {
    return localStorage.getItem(HELP_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}
</script>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  background: var(--modal-veil);
  display: flex;
  align-items: flex-end;
  z-index: 30;
  animation: fade 0.22s ease-out;
  backdrop-filter: blur(3px);
}

@keyframes fade { from { opacity: 0; } to { opacity: 1; } }

.sheet {
  width: 100%;
  background: var(--surface);
  border-radius: var(--r-card-lg) var(--r-card-lg) 0 0;
  padding: 20px 18px calc(18px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 94%;
  overflow-y: auto;
  animation: slide-up 0.38s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes slide-up { from { transform: translateY(100%); } to { transform: none; } }

.grab { width: 36px; height: 4px; border-radius: 2px; background: var(--line-2); margin: -6px auto 0; }

header { display: grid; gap: 3px; }

header h3 {
  margin: 0;
  font-family: var(--f-title);
  font-size: 20px;
  font-variation-settings: 'wdth' 112, 'wght' 800;
}

.stage {
  display: grid;
  gap: 10px;
  justify-items: center;
  padding: 16px 12px;
  border-radius: var(--r-button);
  border: 1px solid var(--line);
  background: var(--surface-2);
}

.chain { display: flex; flex-wrap: wrap; gap: 6px; justify-content: center; }

.link {
  padding: 4px 9px;
  border-radius: var(--r-pill);
  font-family: var(--f-label);
  font-size: 10.5px;
  letter-spacing: 0.06em;
  border: 1px solid var(--line);
  color: var(--text-3);
  background: var(--surface);
}

.link.ok { border-color: var(--ok); color: var(--ok); }
.link.on { border-color: var(--active); color: var(--active); }

.slots { display: flex; gap: 5px; }

.slot {
  width: 26px;
  height: 32px;
  display: grid;
  place-items: center;
  border-radius: var(--r-field);
  border: 1px solid var(--line-2);
  background: var(--surface);
  font-family: var(--f-title);
  font-size: 14px;
}

.slot.given { border-color: var(--active); color: var(--active); }
.slot.paid { border-color: var(--amber); color: var(--amber); background: var(--amber-veil); }
.slot.cursor { border-color: var(--active); box-shadow: 0 0 0 2px var(--active-veil); }

.account {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-2);
  font-size: 12.5px;
}

.account :deep(b) { color: var(--text); }

.text { margin: 0; color: var(--text-2); font-size: 13.5px; line-height: 1.55; }
.text :deep(b) { color: var(--text); }
.text :deep(i) { color: var(--active); font-style: normal; font-weight: 600; }
.text :deep(.warn) { color: var(--amber); }

.dots { display: flex; gap: 6px; justify-content: center; }

.dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--line-2);
  transition: background 0.2s, width 0.2s;
}

.dots span.on { width: 18px; border-radius: var(--r-pill); background: var(--active); }

.actions { display: flex; gap: 10px; }
.actions .btn { flex: 1; }

@media (min-width: 1024px) {
  .scrim { align-items: center; justify-content: center; }
  .sheet {
    max-width: 460px;
    border-radius: var(--r-card-lg);
    max-height: 88%;
    animation: zoom-in 0.34s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .grab { display: none; }
}

@keyframes zoom-in {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: none; opacity: 1; }
}
</style>
